/* eslint-disable @typescript-eslint/no-empty-function */
import type { PayOverdueBalanceMutation } from '@ctct/state-server-layer';

import type { Listeners } from '../helpers';
import { controllerHelpers } from '../helpers';
import type {
  RouterNonUiManager,
  BillingInfoFormManager,
  PaymentMethodsManager,
  OverduePaymentButtonManager,
  OverdueSummaryManager,
  AlertManager,
  UpdateBillingManager,
} from '../managers';
import { overduePaymentButtonEventBus, paymentMethodsEventBus } from '../managers';

// to prevent even a tiny posibility of sending the request twice
let isPaying = false;

export class OverduePaymentButtonController {
  public listeners: Listeners;

  constructor(
    private paymentMethodsManager: PaymentMethodsManager,
    private billingInfoFormManager: BillingInfoFormManager,
    private routerManager: RouterNonUiManager,
    private overduePaymentButtonManager: OverduePaymentButtonManager,
    private overdueSummaryManager: OverdueSummaryManager,
    private alertManager: AlertManager,
    private updateBillingManager: UpdateBillingManager
  ) {
    const eventConnections = [
      overduePaymentButtonEventBus.bindTo('payOverdueBalanceClicked', this.payOverdueBalanceClicked),
      paymentMethodsEventBus.bindTo('applePayConnected', this.attemptAutoCheckoutOnMobile),
      paymentMethodsEventBus.bindTo('paymentMethodCanBeUpdated', this.onPaymentMethodUpdated),
    ];

    this.listeners = controllerHelpers.createListeners(eventConnections);
  }

  private payOverdueBalanceClicked = async (payOverdueBalanceApiCall: PayOverdueBalanceMutation['mutateAsync']) => {
    if (isPaying) return;

    isPaying = true;

    const {
      paymentMethodsApi,
      billingInfoFormApi,
      overdueSummaryApi,
      alertApi,
      updateBillingApi,
      routerApi,
      overduePaymentButtonApi,
    } = await this.getApis();

    try {
      await this.disableAll();
      alertApi.removeAllAlerts();

      const { currency, amount } = overdueSummaryApi.getOverdue();
      const addr = billingInfoFormApi.getValuesForApiRequest();

      const paymentPayload = await paymentMethodsApi.getPayloadForOverduePayment({
        postalCode: addr.postal_code,
        countryCode: addr.country,
        fullName: `${addr.first_name} ${addr.last_name}`,
        email: addr.email,
        phone: addr.phone,
      });
      const successMessage = overduePaymentButtonApi.getSuccessfulUpdateMessage();
      const billingUpdateErrorMessage = overduePaymentButtonApi.getBillingAddressUpdateErrorMessage();

      try {
        // when paying overdue balance taxAmount and addonAmount are both set to 0
        await payOverdueBalanceApiCall({
          ...paymentPayload,
          amount,
          taxAmount: 0,
          addonAmount: 0,
          currencyCode: currency,
        });
      } catch (payError) {
        if (payError instanceof Error && payError.message) {
          const errorMessage = await controllerHelpers.handleStripePaymentError(payError, paymentMethodsApi);

          alertApi.addAlert({ message: errorMessage, id: errorMessage });

          return;
        }
      }

      if (billingInfoFormApi.isFormDirty()) {
        try {
          const billingInfoPayload = await billingInfoFormApi.getValuesForApiRequest();

          await updateBillingApi.submitBillingChanges({
            customer_info: billingInfoPayload,
          });
        } catch (billingError) {
          // at this point overdue payment is already processed, but billing info update failed
          paymentMethodsApi.resetCurrentPaymentMethodWarning();

          if (billingError instanceof Error && billingError.message) {
            // since billing info was not saved, we need to make sure it is not persisted in session store
            billingInfoFormApi.clearBillingFormData();

            // redirect to billing page with success message for payment and error message for billing info update
            routerApi.goToBillingInfo({
              replace: true,
              alerts: {
                successMessage,
                errorMessage: `${billingUpdateErrorMessage}: ${billingError.message}`,
              },
            });
          }

          return;
        }
      }

      paymentMethodsApi.resetCurrentPaymentMethodWarning();
      routerApi.goToBillingInfo({ replace: true, alerts: { successMessage } });
    } catch (error) {
      if (error instanceof Error && error.message) {
        alertApi.addAlert({
          message: error.message,
          id: error.message,
        });
      }
    } finally {
      await this.enableAll();
      isPaying = false;
    }
  };

  private onPaymentMethodUpdated = async (isUpdated: boolean) => {
    const { overduePaymentButtonApi, paymentMethodsApi } = await this.getApis();

    if (isUpdated) {
      overduePaymentButtonApi.enable();
      paymentMethodsApi.resetCurrentPaymentMethodWarning();
    } else {
      overduePaymentButtonApi.disable();
      paymentMethodsApi.setCurrentPaymentMethodWarning();
    }
  };

  private async disableAll() {
    const { paymentMethodsApi, billingInfoFormApi, overduePaymentButtonApi } = await this.getApis();

    overduePaymentButtonApi.disable();
    paymentMethodsApi.setFullLoading(true);
    billingInfoFormApi.setIsLoading(true);
  }

  private async enableAll() {
    const { paymentMethodsApi, billingInfoFormApi, overduePaymentButtonApi } = await this.getApis();

    overduePaymentButtonApi.enable();
    paymentMethodsApi.setFullLoading(false);
    billingInfoFormApi.setIsLoading(false);
  }

  private attemptAutoCheckoutOnMobile = async () => {
    const { overduePaymentButtonApi } = await this.getApis();

    if (overduePaymentButtonApi.getIsMobile()) {
      overduePaymentButtonApi.triggerButtonClick();
    }
  };

  private async getApis() {
    const [
      paymentMethodsApi,
      billingInfoFormApi,
      routerApi,
      overduePaymentButtonApi,
      overdueSummaryApi,
      alertApi,
      updateBillingApi,
    ] = await Promise.all([
      this.paymentMethodsManager.getApiWhenReady(),
      this.billingInfoFormManager.getApiWhenReady(),
      this.routerManager.getApiWhenReady(),
      this.overduePaymentButtonManager.getApiWhenReady(),
      this.overdueSummaryManager.getApiWhenReady(),
      this.alertManager.getApiWhenReady(),
      this.updateBillingManager.getApiWhenReady(),
    ]);

    return {
      paymentMethodsApi,
      billingInfoFormApi,
      routerApi,
      overduePaymentButtonApi,
      overdueSummaryApi,
      alertApi,
      updateBillingApi,
    };
  }
}
